import { useCallback, useEffect } from "react";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  type Location,
  type NavigationType,
} from "react-router-dom";
import { isSafeInternalPath } from "@/lib/notifListener";

/**
 * In app history ledger: the entries this app session has actually shown,
 * plus a pointer to the current one. The café page back arrow reads it to
 * decide whether a real earlier page of the visitor's sits directly below.
 *
 * Despia arrives from a push with history.pushState + popstate, so React
 * Router sees a POP with key "default" and no idx. We never trust that key:
 * any entry with key "default" is re-stamped in place with a router replace,
 * which gives it a real unique key that later POPs can match.
 */
export type HistoryEntry = {
  key: string;
  pathname: string;
  search: string;
  /** Only the first "/" entry of the session carries this. */
  launch?: boolean;
};

let stack: HistoryEntry[] = [];
let pointer = -1;
let pendingRestamp = false;

export function resetInAppHistory() {
  stack = [];
  pointer = -1;
  pendingRestamp = false;
}

export function getInAppHistorySnapshot() {
  return { stack: stack.map((e) => ({ ...e })), pointer };
}

type Loc = Pick<Location, "key" | "pathname" | "search">;

function entryOf(loc: Loc): HistoryEntry {
  return { key: loc.key, pathname: loc.pathname, search: loc.search };
}

function pushEntry(entry: HistoryEntry, byRouter: boolean) {
  stack = stack.slice(0, pointer + 1);
  // A router push on top of "/" means it was a real page of theirs. A foreign
  // (push notification) arrival directly on top of it keeps the launch mark.
  if (byRouter && pointer >= 0 && stack[pointer]) delete stack[pointer].launch;
  stack.push(entry);
  pointer = stack.length - 1;
}

/**
 * Record one router location change. Returns restamp: true when the caller
 * must replace the current entry in place to give it a real router key.
 */
export function recordLocation(loc: Loc, type: NavigationType): { restamp: boolean } {
  if (pendingRestamp && type === "REPLACE" && stack[pointer]?.pathname === loc.pathname) {
    pendingRestamp = false;
    stack[pointer] = { ...stack[pointer], key: loc.key, search: loc.search };
    return { restamp: false };
  }
  pendingRestamp = false;

  if (stack.length === 0) {
    const entry = entryOf(loc);
    if (loc.pathname === "/") entry.launch = true;
    stack = [entry];
    pointer = 0;
    return { restamp: loc.key === "default" };
  }

  if (type === "PUSH") {
    pushEntry(entryOf(loc), true);
    return { restamp: false };
  }

  if (type === "REPLACE") {
    // Never inherits launch.
    stack[pointer] = entryOf(loc);
    return { restamp: false };
  }

  // POP: match by router key only, never by path.
  if (loc.key !== "default") {
    const idx = stack.findIndex((e) => e.key === loc.key);
    if (idx !== -1) {
      pointer = idx;
      return { restamp: false };
    }
  }
  // Foreign arrival (Despia pushState + popstate).
  pushEntry(entryOf(loc), false);
  return { restamp: true };
}

export function markRestampPending() {
  pendingRestamp = true;
}

export function getEntryBelow(): HistoryEntry | null {
  return pointer > 0 ? stack[pointer - 1] ?? null : null;
}

export type BackDecision = { kind: "back" } | { kind: "to"; path: string };

export function decideBack(currentPathname: string, fromPath?: unknown): BackDecision {
  const below = getEntryBelow();
  const usable =
    !!below &&
    below.pathname !== "/auth" &&
    below.pathname !== currentPathname &&
    !(below.pathname === "/" && below.launch);
  if (usable) return { kind: "back" };
  const from =
    isSafeInternalPath(fromPath) &&
    !fromPath.startsWith("/auth") &&
    fromPath.split(/[?#]/)[0] !== currentPathname
      ? fromPath
      : null;
  return { kind: "to", path: from ?? "/browse" };
}

/** Mounted once inside BrowserRouter. */
export function InAppHistoryTracker() {
  const location = useLocation();
  const type = useNavigationType();
  const navigate = useNavigate();
  useEffect(() => {
    const { restamp } = recordLocation(location, type);
    if (restamp) {
      markRestampPending();
      navigate(location.pathname + location.search + location.hash, {
        replace: true,
        state: location.state,
      });
    }
    // Keyed on location.key: every entry change, including re-stamps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  return null;
}

/** The café page back arrow handler. */
export function useCafeBack() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    const d = decideBack(location.pathname, (location.state as { from?: unknown } | null)?.from);
    if (d.kind === "back") navigate(-1);
    else navigate(d.path, { replace: true });
  }, [navigate, location.pathname, location.state]);
}
